import { Component, Inject, OnInit, ChangeDetectorRef, ElementRef, ViewChild, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FlexLayoutDirectiveModule } from '../../directives/flex-layout-directive.module';
import { ImageCropperModule } from 'ngx-image-cropper';
import { BehaviorSubject, Subscription, Observable, Observer } from 'rxjs';
import { RequestService, LayoutUtilsService, LoaderService } from '../../../shared/services';
import { urlSafeBase64Encoding } from '../../../shared/helpers';
import { ImageCroppedEvent, ImageCropperComponent } from 'ngx-image-cropper';
import { environment } from '../../../../environments/environment';

interface ViewDialogData {
	title: string;
	data: any;
	currentImage: string;
	profile: boolean;
}

@Component({
	selector: 'app-crop-dialog-modal',
	standalone: true,
	imports: [
		CommonModule,
		MatDialogModule,
		MatButtonModule,
		TranslateModule,
		FlexLayoutDirectiveModule,
		ImageCropperModule
	],
	templateUrl: './custom-crop-dialog.component.html',
	styleUrls: ['./custom-crop-dialog.component.scss']
})
export class ModalCropDialogComponent implements OnInit {
	private subscriptions: Subscription[] = [];
	public orgType = environment.orgType;
	public originalImage = undefined;
	public selectedUser: any;
	public errorMessage: string = '';
	public isProfile: boolean = false;
	public targetUserId: string = undefined;
	public passingThru: boolean = false;
	public loading: boolean = false;
	public hasFormErrors: boolean = false;
	public dataTypeDisplay!: string;
	public dataTypeDisplayFolder!: string;
	public dataList: any[] = [];
	public dataListFolder: any[] = [];
	public imageSelected: any = undefined;
	public imageUrlPath: any = undefined;
	/** Bound to template for cropper; updated in next tick to avoid NG0100 when replacing image. */
	public displayImageBase64: string | undefined = undefined;
	/** Stable value passed to image-cropper [imageBase64]; set only in same deferred block as showCropper to avoid NG0100. */
	public cropperImageBase64: string | undefined = undefined;
	/** True only after initial pictureLink is set; avoids NG0100 when opening (default → actual URL). */
	public dialogReady: boolean = false;
	/** True when dialog content should show the img (pictureLink, no base64). Set only in deferred blocks to avoid NG0100. */
	public showImageTabContainer: boolean = false;
	/** True when dialog content should show the cropper. Set only in deferred blocks to avoid NG0100. */
	public showCropper: boolean = false;
	/** True when both imageUrlPath and imageSelected are set (crop/clear buttons). Set only in deferred blocks to avoid NG0100. */
	public showCropActions: boolean = false;
	private imageLoadTimeoutId: any = null;
	public selectedRatio: number = 1;
	public folderText: string = '';
	public folderSelected: string = '';
	public tabSelected: number = 0;
	public searchVal: string = '';
	public imageChangedEvent: any = '';
	public croppedImage: any = '';
	public allowedExtensions: string[] = ['jpeg', 'jpg', 'bmp', 'png'];

	public defaultPictureLink: string = 'assets/images/profile.png';
	public pictureLink: string = JSON.parse(JSON.stringify(this.defaultPictureLink));

	public paginatorTotal: BehaviorSubject<number> = new BehaviorSubject<number>(0);
	pageSize = 10;
	pageNumber = 1;

	orderDir = 'asc';
	orderBy = '_id'; // uid

	@ViewChild(ImageCropperComponent) imageCropper: ImageCropperComponent;
	constructor(private zone: NgZone, private translate: TranslateService,
		private requestService: RequestService,
		private cdr: ChangeDetectorRef, private loaderService: LoaderService,
		private layoutUtilsService: LayoutUtilsService,
		public dialogRef: MatDialogRef<ModalCropDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: ViewDialogData) {
		// console.log('ModalGalleryDialogComponent', data);
		this.isProfile = data.profile || false;
	}

	ngOnInit() {
		// Reset so each open (including second time) starts in loading state and avoids NG0100
		this.dialogReady = false;
		this.displayImageBase64 = undefined;
		this.showImageTabContainer = false;
		this.showCropper = false;
		this.showCropActions = false;
		this.cropperImageBase64 = undefined;
		this.imageUrlPath = undefined;

		this.dataTypeDisplay = this.translate.instant('Image');
		this.dataTypeDisplayFolder = this.translate.instant('Folder');

		let pictureLink = '';
		if (this.data.data && this.data.data.pictureLink && this.data.data.pictureLink !== '') {
			pictureLink = this.data.data.pictureLink;
		}
		if (this.data.data && this.data.data._id) {
			this.targetUserId = this.data.data._id;
		}

		if (pictureLink && pictureLink !== '') {
			this.originalImage = pictureLink;
			this.pictureLink = pictureLink;
			// Load base64; dialogReady will be set in scheduleDisplayImageUpdate (loading → cropper in one step)
			this.getBase64ImageFromFile(this.pictureLink);
			this.imageSelected = { error: false, text: this.getFilename(this.pictureLink), originalFile: undefined, source_url: null };
		}

		// Subscribe to currentUserSubject for profile mode or to get user's pictureLink
		this.subscriptions.push(
			this.requestService.currentUserSubject.subscribe((data) => {
				if (data) {
					this.selectedUser = data;

					if (this.isProfile) {
						this.targetUserId = this.selectedUser._id;
						if (data.pictureLink && data.pictureLink !== '' && !this.pictureLink) {
							this.originalImage = data.pictureLink;
							this.pictureLink = data.pictureLink;
						}
					}

					if (this.pictureLink && this.pictureLink !== '' && !this.imageUrlPath) {
						this.getBase64ImageFromFile(this.pictureLink);
						this.imageSelected = { error: false, text: this.getFilename(this.pictureLink), originalFile: undefined, source_url: null };
					} else {
						setTimeout(() => {
							this.dialogReady = true;
							this.showImageTabContainer = true;
							this.showCropper = false;
							setTimeout(() => this.cdr.detectChanges(), 0);
						}, 0);
					}
				} else {
					setTimeout(() => {
						this.dialogReady = true;
						this.showImageTabContainer = true;
						this.showCropper = false;
						setTimeout(() => this.cdr.detectChanges(), 0);
					}, 0);
				}
			})
		);
		// When no pictureLink, show dialog with default img after next macrotask (avoids NG0100)
		if (!pictureLink || pictureLink === '') {
			setTimeout(() => {
				if (!this.dialogReady) {
					this.dialogReady = true;
					this.showImageTabContainer = true;
					this.showCropper = false;
					setTimeout(() => this.cdr.detectChanges(), 0);
				}
			}, 0);
		}
	}
	public rationChanged(event) {
		this.selectedRatio = Number(event.value);
	}
	/**
	 * On Destroy
	 */
	ngOnDestroy() {
		this.clearImageLoadTimeout();
		this.subscriptions.forEach(el => el.unsubscribe());
	}
	/** Updates in macrotask; showCropper in next macrotask so [imageBase64] is stable when cropper is created (avoids NG0100). */
	private scheduleDisplayImageUpdate(): void {
		this.clearImageLoadTimeout();
		setTimeout(() => {
			this.displayImageBase64 = this.imageUrlPath;
			this.cropperImageBase64 = this.imageUrlPath;
			this.dialogReady = true;
			this.showImageTabContainer = false;
			this.showCropActions = true;
			setTimeout(() => {
				this.showCropper = true;
				this.cdr.detectChanges();
			}, 0);
		}, 0);
	}
	private clearImageLoadTimeout(): void {
		if (this.imageLoadTimeoutId != null) {
			clearTimeout(this.imageLoadTimeoutId);
			this.imageLoadTimeoutId = null;
		}
	}
	public getBase64ImageFromFile(imageUrl) {
		// Fallback: if image never loads (CORS, timeout), show img with URL after 6s
		this.clearImageLoadTimeout();
		this.imageLoadTimeoutId = setTimeout(() => {
			this.imageLoadTimeoutId = null;
			if (!this.dialogReady) {
				setTimeout(() => {
					this.dialogReady = true;
					this.showImageTabContainer = true;
					this.showCropper = false;
					setTimeout(() => this.cdr.detectChanges(), 0);
				}, 0);
			}
		}, 6000);

		this.getBase64ImageFromURL(imageUrl).subscribe(base64data => {
			const dataUrl = 'data:image/png;base64,' + base64data;
			this.imageUrlPath = dataUrl;
			this.scheduleDisplayImageUpdate();
		},
			err => {
				this.clearImageLoadTimeout();
				this.layoutUtilsService.showNotification(this.translate.instant('Error:') + ' ' + this.translate.instant('The image is not accessible'), this.translate.instant('Dismiss'));
				setTimeout(() => {
					this.dialogReady = true;
					setTimeout(() => this.cdr.detectChanges(), 0);
				}, 0);
			}
		);
	}
	closeModal(data): void {
		this.zone.run(() => {
			this.dialogRef.close(data);
		});
	}
	goToCrop(): void {
		this.zone.run(() => {
			// console.log(this.imageUrlPath);
			this.tabSelected = 1;
		});
	}
	getFilename(url) {
		if (url) {
			var m = url.toString().match(/.*\/(.+?)\./);
			if (m && m.length > 1) {
				return m[1];
			}
		}
		return "";
	}
	onBrowseFiles(target: any): void {
		this.readFiles(target.files);
	}
	/**
	 *  @param files: list of browsed files
	 *  @param index: iterator over browsed images
	 *
	 *  read files browsed by user
	 */
	readFiles(files, index = 0): void {
		let reader = new FileReader();
		if (index in files) {
			let currentFile = { error: false, text: files[index].name.split('.')[0], id: files[index].id, originalFile: files[index], source_url: null };
			let fileExt = files[index].name.split('.').pop();
			const max_size = 5000000;
			const max_height = 600;
			const max_width = 600;
			if (files[index].size > max_size) {
				this.layoutUtilsService.showNotification(this.translate.instant('Maximum size allowed is') + ' ' + max_size / 1000000 + 'MB', this.translate.instant('Dismiss'));
			} else if (this.allowedExtensions.indexOf(fileExt.toLowerCase()) === -1) {
				currentFile.error = true;
				this.layoutUtilsService.showNotification(this.translate.instant('The file type is not allowed'), this.translate.instant('Dismiss'));
			} else {
				this.readFile(files[index], reader, (event) => {
					this.loaderService.display(true);
					var image = new Image();
					this.readImage(event, image, (imgresult) => {
						// console.log('readImage', imgresult);
						var canvas = document.createElement("canvas");
						var context = canvas.getContext("2d");

						var ratio = Math.min(max_width / imgresult.width, max_height / imgresult.height);


						// console.log('ratio', ratio);
						if (imgresult.width < imgresult.width * ratio && imgresult.height < imgresult.height * ratio) {
							canvas.width = imgresult.width;
							canvas.height = imgresult.height;
						} else {
							canvas.width = imgresult.width * ratio;
							canvas.height = imgresult.height * ratio;
						}
						context.drawImage(image,
							0,
							0,
							image.width,
							image.height,
							0,
							0,
							canvas.width,
							canvas.height
						);
						// console.log(canvas.width, canvas.height);
						// console.log(image.width, max_width, image.height, max_height);
						if (canvas.width <= max_width && canvas.height <= max_height) {
							currentFile['originalFile'] = this.dataURLtoFile(canvas.toDataURL("image/png", 0.75), currentFile.text + '.png');
							const dataUrl = canvas.toDataURL("image/png", 0.75);
							this.imageUrlPath = dataUrl;
							this.imageSelected = currentFile;
							this.pictureLink = this.imageUrlPath;
							// Set base64 in first macrotask; show cropper in second so [imageBase64] is stable when cropper is created (avoids NG0100)
							setTimeout(() => {
								this.displayImageBase64 = dataUrl;
								this.cropperImageBase64 = dataUrl;
								this.showImageTabContainer = false;
								this.showCropActions = true;
								this.loaderService.display(false);
								setTimeout(() => {
									this.showCropper = true;
									this.cdr.detectChanges();
								}, 0);
							}, 0);
						} else {
							this.layoutUtilsService.showNotification(this.translate.instant('The image is not well formatted'), this.translate.instant('Dismiss'));
							this.loaderService.display(false);
						}
						// this.continueUpload(currentFile);
					});
				});
			}
		} else {
			this.cdr.detectChanges();
		}
	}
	dataURLtoFile(dataurl, filename) {
		if (!dataurl || typeof dataurl !== 'string') {
			throw new Error('Invalid data URL provided');
		}
		var arr = dataurl.split(',');
		if (arr.length < 2) {
			throw new Error('Invalid data URL format');
		}
		var mimeMatch = arr[0].match(/:(.*?);/);
		if (!mimeMatch || mimeMatch.length < 2) {
			throw new Error('Invalid MIME type in data URL');
		}
		var mime = mimeMatch[1];
		var bstr = atob(arr[1]);
		var n = bstr.length;
		var u8arr = new Uint8Array(n);

		while (n--) {
			u8arr[n] = bstr.charCodeAt(n);
		}

		return new File([u8arr], filename, { type: mime });
	}
	readFile(file, reader, callback): void {
		reader.onload = () => {
			callback(reader.result);
		}
		reader.readAsDataURL(file);
	}
	readImage(file, image, callback): void {
		image.onload = () => {
			callback(image);
		}
		image.src = file;
	}
	continueUpload(currentFile) {
		if (!this.loading && currentFile && currentFile.originalFile) {
			// Defer to next macrotask so [disabled]="loading" does not change during current CD (avoids NG0100)
			setTimeout(() => {
				this.loading = true;
				this.errorMessage = '';
				this.requestService.onUploadUserImage(currentFile, this.targetUserId)
					.subscribe(
						(results: any) => {
							if (results['status']) {
								currentFile.source_url = results.results.pictureLink;
								this.pictureLink = results.results.pictureLink;
								this.layoutUtilsService.showNotification(this.translate.instant('User image uploaded successfully'), this.translate.instant('Dismiss'));

								if (this.isProfile) {
									this.requestService.updateUserData('pictureLink', results.results.pictureLink);
								}
								this.loading = false;
								this.closeModal(results.results.pictureLink);
							} else {
								this.loading = false;
								currentFile.error = true;
								this.layoutUtilsService.showNotification('Error:' + results['message'], 'Dismiss');
							}
							this.cdr.detectChanges();
						},
						(error: any) => {
							this.loading = false;
							currentFile.error = true;
							this.layoutUtilsService.showNotification(this.translate.instant('Error:') + ' ' + this.translate.instant('Error uploading file'), this.translate.instant('Dismiss'));
							this.cdr.detectChanges();
						}
					);
			}, 0);
		} else {
			this.closeModal(undefined);
		}
	}
	// fileChangeEvent(event: any): void {
	//     this.imageChangedEvent = event;
	// }
	cropIt() {
		// Request base64 output explicitly; crop('base64') returns ImageCroppedEvent with base64
		let croppedImage = this.imageCropper?.crop?.('base64') ?? null;
		let base64Data = croppedImage?.base64 || this.croppedImage;
		// Fallback: use current image (e.g. newly selected file) if cropper not ready yet
		if (!base64Data && this.imageSelected && this.imageUrlPath) {
			base64Data = this.imageUrlPath;
		}
		if (!this.loading && this.imageSelected && base64Data) {
			try {
				this.imageSelected['originalFile'] = this.dataURLtoFile(base64Data, this.imageSelected.text + '.jpeg');
				this.continueUpload(this.imageSelected);
			} catch (error) {
				this.layoutUtilsService.showNotification(this.translate.instant('Error:') + ' ' + (error.message || this.translate.instant('Could not process cropped image.')), this.translate.instant('Dismiss'));
			}
		} else {
			this.layoutUtilsService.showNotification(this.translate.instant('Error:') + ' ' + this.translate.instant('Please select and crop an image first.'), this.translate.instant('Dismiss'));
		}
	}
	imageCropped(event: ImageCroppedEvent) {
		this.croppedImage = event.base64;
	}
	imageHasLoaded(e) {
		//  console.log('imageHasLoaded', e);
	}
	imageLoaded() {
		// show cropper
	}
	cropperReady() {
		// cropper ready
	}
	loadImageFailed() {
		// show message
	}
	public clearIt(e) {
		if (e) {
			e.stopImmediatePropagation();
			e.preventDefault();
			// e.stopPropagation();
		}
		if (!this.loading) {
			const _title: string = this.dataTypeDisplay + ' ' + this.translate.instant('Deletion');
			const _description: string = this.translate.instant('Are you sure you want to permanently delete this') + ' ' + this.dataTypeDisplay + '?';
			const _waitDesciption: string = this.translate.instant('Deleting') + '...';

			const dialogRef = this.layoutUtilsService.deleteElement(_title, _description, _waitDesciption);
			dialogRef.afterClosed().subscribe(res => {
				if (!res) {
					return;
				}
				this.realDelete();
			});
		}
	}
	public realDelete() {
		const _deleteMessage = this.dataTypeDisplay + ' ' + this.translate.instant('Deleted Successfully') + '.';
		if (!this.loading) {
			// Defer to next macrotask so [disabled]="loading" does not change during current CD (avoids NG0100)
			setTimeout(() => {
				this.loading = true;
				this.errorMessage = '';
				this.requestService.deleteProfileImage('user', this.targetUserId, (data, error) => {
					if (error) {
						this.errorMessage = error;
						this.layoutUtilsService.showNotification(this.translate.instant('Error:') + error, this.translate.instant('Dismiss'));
					}
					this.loading = false;
					if (data) {
						this.layoutUtilsService.showNotification(_deleteMessage, this.translate.instant('Dismiss'));
						// Defer all template-bound updates to next macrotask so [imageBase64] and *ngIf do not change during same CD (avoids NG0100)
						setTimeout(() => {
							this.imageSelected = undefined;
							this.imageUrlPath = undefined;
							this.displayImageBase64 = undefined;
							this.cropperImageBase64 = undefined;
							this.pictureLink = JSON.parse(JSON.stringify(this.defaultPictureLink));
							this.showImageTabContainer = true;
							this.showCropper = false;
							this.showCropActions = false;
							if (this.isProfile) {
								this.requestService.updateUserData('pictureLink', '');
							}
							this.cdr.detectChanges();
							this.closeModal(this.defaultPictureLink);
						}, 0);
					}
				});
			}, 0);
		}
	}
	getBase64ImageFromURL(url: string) {
		return Observable.create((observer: Observer<string>) => {
			// create an image object
			let img = new Image();
			img.crossOrigin = 'Anonymous';
			img.src = url + '?t=' + new Date().getTime();
			if (!img.complete) {
				// This will call another method that will create image from url
				img.onload = () => {
					observer.next(this.getBase64Image(img));
					observer.complete();
				};
				img.onerror = (err) => {
					observer.error(err);
				};
			} else {
				observer.next(this.getBase64Image(img));
				observer.complete();
			}
		});
	}
	getBase64Image(img: HTMLImageElement) {
		// We create a HTML canvas object that will create a 2d image
		var canvas = document.createElement("canvas");
		canvas.width = img.width;
		canvas.height = img.height;
		var ctx = canvas.getContext("2d");
		// This will draw image
		ctx.drawImage(img, 0, 0);
		// Convert the drawn image to Data URL
		var dataURL = canvas.toDataURL("image/png", 0.75);
		return dataURL.replace(/^data:image\/(png|jpg|jpeg);base64,/, "");
	}
}
